package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.feature.auth.data.remote.ApiException
import com.milta.feature.auth.domain.entity.OAuthAuthenticateOutcome
import com.milta.feature.auth.domain.entity.OAuthProvider
import com.milta.feature.auth.domain.repository.AuthRepository
import com.milta.feature.auth.presentation.OtpIntent
import com.milta.feature.auth.presentation.OtpViewModel
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

private const val PRIMARY = "#2E5F7A"
private const val TEXT_PRIMARY = "#1E3A4A"
private const val TEXT_SECONDARY = "#6B8A9A"
private const val SURFACE_DIM = "#E8EFF3"
private const val ERROR = "#D32F2F"
private const val BORDER = "#D7DFE3"

/** Shared centered-card frame for the auth cascade screens after Welcome — same visual language
 * (form panel background/card width) without repeating the split-screen brand panel. */
@Composable
fun WebFormFrame(content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                width(100.percent)
                property("min-height", "100vh")
                property("align-items", "center")
                property("justify-content", "center")
                property("background", SURFACE_DIM)
                property("box-sizing", "border-box")
            }
        },
    ) {
        Div(attrs = { style { width(380.px); property("max-width", "100%"); padding(24.px); property("box-sizing", "border-box") } }) {
            content()
        }
    }
}

@Composable
private fun ScreenTitle(text: String) {
    Div(
        attrs = { style { property("color", TEXT_PRIMARY); property("font-size", "22px"); property("font-weight", "700"); property("margin-bottom", "8px") } },
    ) { Text(text) }
}

@Composable
private fun ScreenSubtitle(text: String) {
    Div(
        attrs = { style { property("color", TEXT_SECONDARY); property("font-size", "14px"); property("margin-bottom", "24px") } },
    ) { Text(text) }
}

@Composable
private fun WebTextField(label: String, value: String, onValueChange: (String) -> Unit) {
    Div(attrs = { style { property("margin-bottom", "12px") } }) {
        Div(attrs = { style { property("color", TEXT_SECONDARY); property("font-size", "12px"); property("margin-bottom", "4px") } }) { Text(label) }
        Input(
            type = InputType.Text,
            attrs = {
                value(value)
                onInput { event -> onValueChange(event.value) }
                style {
                    width(100.percent)
                    height(42.px)
                    property("border-radius", "8px")
                    property("border", "1px solid $BORDER")
                    property("padding", "0 12px")
                    property("font-size", "14px")
                    property("box-sizing", "border-box")
                }
            },
        )
    }
}

@Composable
private fun WebPrimaryButton(text: String, enabled: Boolean, onClick: () -> Unit) {
    Button(
        attrs = {
            if (enabled) onClick { onClick() }
            style {
                width(100.percent)
                height(46.px)
                property("border-radius", "10px")
                property("border", "none")
                property("background", PRIMARY)
                property("color", "#FFFFFF")
                property("font-size", "14px")
                property("font-weight", "600")
                property("cursor", if (enabled) "pointer" else "default")
                property("opacity", if (enabled) "1" else "0.6")
            }
        },
    ) { Text(text) }
}

@Composable
private fun WebLinkText(text: String, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style { property("color", PRIMARY); property("font-size", "13px"); property("cursor", "pointer"); property("text-align", "center"); property("margin-top", "16px") }
        },
    ) { Text(text) }
}

@Composable
private fun ErrorText(text: String?) {
    if (text == null) return
    Div(attrs = { style { property("color", ERROR); property("font-size", "13px"); property("margin-bottom", "12px") } }) { Text(text) }
}

@Composable
fun WebEditPhoneScreen(
    provider: OAuthProvider,
    idToken: String,
    onBack: () -> Unit,
    onUpdated: (phoneNumber: String) -> Unit,
) {
    val strings = LocalStrings.current
    val repository: AuthRepository = koinInject()
    val scope = rememberCoroutineScope()

    var dialCode by remember { mutableStateOf("+32") }
    var localNumber by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    WebFormFrame {
        ScreenTitle(strings.auth.otpWrongNumber)
        WebTextField("${strings.auth.phoneNumber} ($dialCode)", localNumber) { localNumber = it; error = null }
        ErrorText(error)
        WebPrimaryButton(strings.auth.registerButton, enabled = !isLoading) {
            error = null
            isLoading = true
            scope.launch {
                repository.updatePendingPhone(provider, idToken, dialCode + localNumber.trim())
                    .onSuccess { outcome ->
                        isLoading = false
                        if (outcome is OAuthAuthenticateOutcome.PhoneVerificationRequired) onUpdated(outcome.phoneNumber)
                    }
                    .onFailure { e ->
                        isLoading = false
                        error = if (e is ApiException && e.message == "error.conflict.phone_number") {
                            strings.auth.errorPhoneConflict
                        } else {
                            strings.auth.errorGeneric
                        }
                    }
            }
        }
        WebLinkText(strings.common.back, onBack)
    }
}

@Composable
fun WebOtpScreen(phone: String, onBack: () -> Unit, onVerified: (role: String) -> Unit) {
    val strings = LocalStrings.current
    val repository: AuthRepository = koinInject()
    val viewModel = remember(phone) { OtpViewModel(phone = phone, repository = repository) }
    val state by viewModel.state.collectAsState()

    WebFormFrame {
        ScreenTitle(strings.auth.otpTitle)
        ScreenSubtitle("${strings.auth.otpSubtitle} $phone")

        WebTextField(strings.auth.otpTitle, state.code) { value ->
            if (value.length <= 6 && value.all(Char::isDigit)) viewModel.onIntent(OtpIntent.UpdateCode(value))
        }

        val errorText = when (state.error) {
            null -> null
            "otp_expired" -> strings.auth.errorExpiredOtp
            "otp_too_many_attempts" -> strings.auth.errorTooManyAttempts
            "otp_resend_limit" -> strings.auth.errorResendLimit
            "otp_invalid" -> strings.auth.errorInvalidOtp
            else -> state.error
        }
        ErrorText(errorText)

        WebPrimaryButton(strings.auth.otpVerifyButton, enabled = state.code.length == 6 && !state.isLoading) {
            viewModel.onIntent(OtpIntent.Verify)
        }

        Div(attrs = { style { property("text-align", "center"); property("margin-top", "16px"); property("font-size", "13px"); property("color", TEXT_SECONDARY) } }) {
            if (state.resendCountdown > 0) {
                Text(strings.auth.otpResendIn.replace("%d", state.resendCountdown.toString()))
            } else {
                Text(strings.auth.otpResendPrefix + " ")
                Div(
                    attrs = {
                        onClick { if (!state.isResendBlocked) viewModel.onIntent(OtpIntent.Resend) }
                        style { property("display", "inline"); property("color", PRIMARY); property("cursor", "pointer") }
                    },
                ) { Text(strings.auth.otpResend) }
            }
        }

        WebLinkText(strings.auth.otpWrongNumber + " " + strings.auth.otpModifyLink, onBack)
    }

    androidx.compose.runtime.LaunchedEffect(viewModel) {
        viewModel.navEvent.collect { event ->
            when (event) {
                is com.milta.feature.auth.presentation.OtpNavEvent.NavigateToHome -> onVerified(event.role)
                com.milta.feature.auth.presentation.OtpNavEvent.NavigateBack -> onBack()
            }
        }
    }
}
